import type { ChangeEvent, FocusEventHandler, MouseEventHandler } from 'react';
import { Suspense, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { CloseBold } from '@elfsight/icons';
import { LazyDatepicker } from '@elfsight/lib-datepicker';
import { Icon } from '@elfsight/lib-icon';
import { Popout } from '@elfsight/widget-modal';
import { TextControl, type TextControlBaseInstance } from '@elfsight/widget-ui';
import {
  createReadableClass,
  getColorWithAlpha,
  getInvertedColor
} from '@elfsight/widget-utils';
import { format } from 'date-fns';
import type { CommonFieldProps } from '../types';

export type DateComponentProps = CommonFieldProps & {
  dateFormat?: string;
};

export function DateComponent({
  value,
  dateFormat = 'auto',
  onChange,
  label,
  language,
  fieldColor = '#000000',
  showLabel = true,
  fieldAccentColor,
  fieldFontStyle = { fontSize: 15 },
  fieldLabelPosition,
  fieldLabelFontStyle,
  fieldLabelColor,
  fieldFillColor,
  fieldBorderType,
  fieldBorderColor,
  fieldBorderWidth,
  fieldBorderRadius,
  shadow,
  ...forwardingProps
}: DateComponentProps) {
  const { fontSize } = fieldFontStyle;

  const [showDatepicker, setShowDatepicker] = useState(false);
  const [rawDateValue, setRawDateValue] = useState<Date | undefined>(undefined);
  const [anchorElement, setAnchorElement] = useState<HTMLDivElement | null>(
    null
  );
  const popupRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<TextControlBaseInstance>(null);
  const locale = language?.language ?? 'en';

  const [input] = Array.from(
    inputRef.current?.containerRef.current?.getElementsByTagName('input') || []
  );

  const handleFocus = () => {
    setShowDatepicker(true);
  };

  const handleClose = () => setShowDatepicker(false);

  const handleChange = (date: Date | undefined) => {
    setRawDateValue(date);
    setShowDatepicker(false);
  };

  const handleBlur: FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (event) => {
    const { relatedTarget } = event;

    const lightDomCondition =
      !shadow && relatedTarget && !relatedTarget.closest('.react-calendar');

    const rootNode = popupRef.current?.getRootNode();
    const shadowDomCondition =
      shadow &&
      (!(rootNode instanceof ShadowRoot) || rootNode.host !== relatedTarget);
    if (lightDomCondition || shadowDomCondition) {
      handleClose();
    }
  };

  const handleClear: MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    setShowDatepicker(false);
    setRawDateValue(undefined);

    const fakeEvent = {
      target: { value: '' } as HTMLInputElement
    } as ChangeEvent<HTMLInputElement>;
    onChange?.('', fakeEvent);
  };

  useEffect(() => {
    if (rawDateValue) {
      let appliedDateFormat = dateFormat;

      if (dateFormat === 'auto' || !dateFormat) {
        appliedDateFormat = getDateFormatForLocale(locale);
      }

      const formattedDate = format(rawDateValue, appliedDateFormat);

      const fakeEvent = {
        target: { value: formattedDate }
      } as ChangeEvent<HTMLInputElement>;

      onChange?.(formattedDate, fakeEvent);
    }
  }, [dateFormat, rawDateValue, locale]);

  function getDateFormatForLocale(locale: string) {
    const formatter = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric'
    });

    const parts = formatter.formatToParts(new Date());

    const firstPart = parts.find(
      (part) => part.type === 'month' || part.type === 'day'
    );

    if (firstPart && firstPart.type === 'day') {
      return 'dd.MM.yyyy';
    }

    return 'MM.dd.yyyy';
  }

  useEffect(() => {
    if (!showDatepicker) {
      input?.blur();
    }
  }, [showDatepicker, input]);

  const icon = value ? (
    <IconWrapper onClick={handleClear} _color={fieldColor}>
      <Icon size={10} component={CloseBold} />
    </IconWrapper>
  ) : undefined;

  return (
    <>
      <DatepickerContainer ref={setAnchorElement}>
        <DateInputControl
          {...forwardingProps}
          ref={inputRef}
          type="text"
          label={
            showLabel && fieldLabelPosition === 'inside' ? label : undefined
          }
          labelFont={fieldLabelFontStyle}
          labelColor={fieldLabelColor}
          color={fieldColor}
          background={fieldFillColor}
          font={fieldFontStyle}
          borderType={fieldBorderType ? fieldBorderType : undefined}
          borderColor={fieldBorderColor}
          borderWidth={fieldBorderWidth}
          borderRadius={fieldBorderRadius}
          value={value || ''}
          onFocus={handleFocus}
          onBlur={handleBlur}
          rightSlot={icon}
        />
        {showDatepicker && <CloseBackdrop onClick={handleClose} />}
      </DatepickerContainer>

      {!!anchorElement && (
        <Popout
          ref={popupRef}
          show={showDatepicker}
          offsetTop={4}
          anchorElement={anchorElement}
          onCloseRequest={handleClose}
          portalContainerElement={document.body}
          shadow={shadow}
          preventCloseOnAnchorElementClick
          closeOnOutsideClick
        >
          <Suspense fallback={null}>
            <LazyDatepicker
              startDate={rawDateValue}
              locale={locale}
              fontSize={fontSize}
              fontColor={fieldColor}
              accentColor={fieldAccentColor}
              backgroundColor={getInvertedColor(fieldColor, [
                'rgb(255, 255, 255)',
                '#292929'
              ])}
              onChange={handleChange}
            />
          </Suspense>
        </Popout>
      )}
    </>
  );
}

const DatepickerContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'date-datepicker-container'
  })
})`
  position: relative;
  height: max-content;
`;

const DateInputControl = styled(TextControl).attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'date-input-control'
  })
})`
  &,
  & * {
    cursor: pointer;
  }

  & input {
    caret-color: transparent;
    pointer-events: none;
  }
`;

const CloseBackdrop = styled.div.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'date-close-backdrop'
  })
})`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: transparent;
  cursor: pointer;
  z-index: 2;
`;

const IconWrapper = styled.div.attrs<{ _color: string }>({
  className: createReadableClass({
    component: 'fields',
    element: 'date-icon-wrapper'
  })
})`
  width: 20px;
  height: 20px;
  border-radius: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 3;
  ${({ _color }) => css`
    background-color: ${getColorWithAlpha(_color, 0.5)};

    &:hover {
      background-color: ${getColorWithAlpha(_color, 0.7)};
    }

    &:active {
      background-color: ${getColorWithAlpha(_color, 0.3)};
    }

    & svg {
      fill: ${getColorWithAlpha(getInvertedColor(_color), 0.7)};
    }

    &:hover svg,
    &:active svg {
      fill: ${getInvertedColor(_color)};
    }
  `}
`;
